import type {
  CaptureDisplaySurface,
  CaptureEndReason,
  CaptureSession,
  CaptureVideoSettings,
  SharedAudioState,
} from './types';

export class BrowserCaptureSession implements CaptureSession {
  private readonly endedListeners = new Set<(reason: CaptureEndReason) => void>();
  private ended = false;

  constructor(
    readonly id: string,
    readonly stream: MediaStream,
    readonly videoTrack: MediaStreamTrack,
    readonly requestedSharedAudio: boolean,
    private readonly didEnd: (session: BrowserCaptureSession) => void,
  ) {
    this.videoTrack.addEventListener('ended', this.handleVideoEnded);
    this.stream.addEventListener('inactive', this.handleStreamInactive);
  }

  get audioTracks(): readonly MediaStreamTrack[] {
    return this.stream.getAudioTracks().filter((track) => track.readyState === 'live');
  }

  get sharedAudioState(): SharedAudioState {
    if (!this.requestedSharedAudio) {
      return 'not-requested';
    }
    return this.audioTracks.length > 0 ? 'available' : 'unavailable';
  }

  get active(): boolean {
    return !this.ended && this.videoTrack.readyState === 'live';
  }

  getSettings(): CaptureVideoSettings {
    const settings = this.videoTrack.getSettings();
    return {
      displaySurface: normalizeDisplaySurface(settings.displaySurface),
      width: finiteOrNull(settings.width),
      height: finiteOrNull(settings.height),
      frameRate: finiteOrNull(settings.frameRate),
      aspectRatio: finiteOrNull(settings.aspectRatio),
    };
  }

  onEnded(listener: (reason: CaptureEndReason) => void): () => void {
    this.endedListeners.add(listener);
    return () => this.endedListeners.delete(listener);
  }

  stop(): void {
    this.finish('stopped');
  }

  private readonly handleVideoEnded = () => {
    this.finish('display-track-ended');
  };

  private readonly handleStreamInactive = () => {
    this.finish('stream-inactive');
  };

  private finish(reason: CaptureEndReason): void {
    if (this.ended) {
      return;
    }

    this.ended = true;
    this.videoTrack.removeEventListener('ended', this.handleVideoEnded);
    this.stream.removeEventListener('inactive', this.handleStreamInactive);
    for (const track of this.stream.getTracks()) {
      stopTrack(track);
    }
    this.didEnd(this);
    for (const listener of this.endedListeners) {
      listener(reason);
    }
    this.endedListeners.clear();
  }
}

function stopTrack(track: MediaStreamTrack): void {
  try {
    track.stop();
  } catch {
    // Continue releasing the remaining tracks if a browser track is already invalid.
  }
}

function normalizeDisplaySurface(value: string | undefined): CaptureDisplaySurface {
  return value === 'monitor' || value === 'window' || value === 'browser' ? value : 'unknown';
}

function finiteOrNull(value: number | undefined): number | null {
  return value !== undefined && Number.isFinite(value) ? value : null;
}
